/**
 * IndexedDB storage for the outbox.
 *
 * WHY IndexedDB and not localStorage: an order with twenty lines and their
 * modifiers is well past what a 5 MB string store handles comfortably, writes
 * must survive a tab crash mid-write, and we need an index on `status` so the
 * replayer can pull only what is pending without deserialising the world.
 *
 * The store is keyed by the client UUID, which is also the value sent to the
 * server as `clientUuid`. One row, one operation, one idempotency key.
 */

import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { OutboxRecord, OutboxStatus } from './types.js';

const DB_NAME = 'sonauto-offline';
const DB_VERSION = 1;
const STORE = 'outbox';
const META = 'meta';

interface OfflineDb extends DBSchema {
  outbox: {
    key: string;
    value: OutboxRecord;
    indexes: {
      by_status: OutboxStatus;
      by_created: number;
    };
  };
  meta: {
    key: string;
    value: number | string | null;
  };
}

export type OfflineDatabase = IDBPDatabase<OfflineDb>;

let dbPromise: Promise<OfflineDatabase> | null = null;

/**
 * Open (once) the shared offline database.
 *
 * Idempotent: every caller gets the same connection promise, so opening from
 * three providers at mount does not race three upgrade transactions.
 */
export function openOfflineDb(): Promise<OfflineDatabase> {
  dbPromise ??= openDB<OfflineDb>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('by_status', 'status');
        store.createIndex('by_created', 'createdAt');
      }
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META);
      }
    },
  });
  return dbPromise;
}

/** Test/teardown hook — forgets the cached connection promise. */
export function resetOfflineDb(): void {
  dbPromise = null;
}

export const OUTBOX_STORE = STORE;
export const META_STORE = META;

/** Is IndexedDB usable here? Safari private mode and some kiosks say no. */
export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}
